import { cloneDeep } from 'lodash';
import { v4 } from 'uuid';

import { type OrderObjectId, type OrderSelection } from '~/types/Order';

import {
  type ExtendedTrackingReference,
  type RequiredTrackingProps,
  type TrackedOrderDetails,
  type TrackingAppLocation,
  type TrackingEventName,
  type TrackingReference,
  type UserDetails,
} from './types';

class TrackingHelper {
  appVersion: 'blackbeard' | 'classic' = 'classic';
  // We get this from the FullStoryMonitor because it's the only way to make a globally available object
  fullStoryEvent?: (
    eventName: string,
    eventProperties: {
      [key: string]: unknown;
    },
  ) => void;
  isBulk = false;
  // The order object ID when only a single order is selected
  orderId?: OrderObjectId;
  orderIds?: OrderObjectId[];
  selectedOrders?: OrderSelection;
  trackedOrderDetailsByShipment?: Record<number, TrackedOrderDetails>;

  userDetails?: UserDetails;

  private extendTrackingReference<E extends TrackingEventName>(
    trackingReference: { initialEventName: E } & TrackingReference,
    requiredProps: RequiredTrackingProps[E],
  ): ExtendedTrackingReference<E> {
    return {
      ...cloneDeep(trackingReference),
      ...cloneDeep(requiredProps),
    };
  }

  private getBaseTrackingReference<E extends TrackingEventName>(
    initialEventName: E,
    appLocation: TrackingAppLocation,
    // TODO: Some events have additional required properties. It would be convenient to stash them in the trackingReference
    // Should we extend the TrackingReference type to include a generic for additional properties?
  ): { initialEventName: E } & TrackingReference {
    return {
      appLocation,
      initialEventName,
      referenceId: this.getReferenceId(),
      ...cloneDeep(this.properties()),
    };
  }

  private getReferenceId() {
    return v4();
  }

  private properties() {
    return {
      appVersionName: this.appVersion,
      isBulk: this.isBulk,
      orderId: this.orderId,
      orderIds: this.orderIds,
      // This count might be incorrect if split orders are selected.
      orderQuantity: this.orderIds?.length || (this.orderId ? 1 : 0),
      selectedOrders: this.selectedOrders,
      trackedOrderDetailsByShipment: this.trackedOrderDetailsByShipment,
      // NOTE: This says "order", it's counting orders, but it does not take Batch Entries into account.
      userDetails: this.userDetails,
    } satisfies Partial<TrackingReference>;
  }

  amendTrackingReferenceOrderSelection<T extends TrackingReference>(
    trackingReference: T,
    selectedOrders?: OrderSelection,
  ): T {
    if (!selectedOrders) return trackingReference;

    const orderIds = Object.keys(selectedOrders);
    const trackingReferenceWithOrderIds = trackingHelper.amendTrackingReferenceOrders(
      trackingReference,
      orderIds,
    );
    trackingReferenceWithOrderIds.selectedOrders = selectedOrders;
    return trackingReferenceWithOrderIds;
  }

  amendTrackingReferenceOrders<T extends TrackingReference>(
    trackingReference: T,
    orderIds?: string[],
  ): T {
    const duped = cloneDeep(trackingReference);
    duped.orderId = undefined;
    duped.orderIds = undefined;
    duped.orderQuantity = undefined;
    duped.isBulk = false;

    if (!orderIds) return duped;

    switch (orderIds.length) {
      case 0: {
        return duped;
      }
      case 1: {
        return {
          ...duped,
          orderId: orderIds[0],
          orderQuantity: 1,
        };
      }
      default: {
        return {
          ...duped,
          isBulk: true,
          orderIds,
          orderQuantity: orderIds.length,
        };
      }
    }
  }

  amendTrackingReferenceTrackedOrderDetailsByShipment<T extends TrackingReference>(
    trackingReference: T,
    trackedOrderDetailsByShipment: Record<number, TrackedOrderDetails>,
  ): T {
    return {
      ...cloneDeep(trackingReference),
      trackedOrderDetailsByShipment,
    };
  }

  appendGlobalProps<T extends object>(input?: T) {
    return {
      ...input,
      ...cloneDeep(this.properties()),
    };
  }

  getTrackingReference<E extends TrackingEventName>(
    initialEventName: E,
    appLocation: TrackingAppLocation,
    requiredProps: RequiredTrackingProps[E],
  ): ExtendedTrackingReference<E> {
    const trackingReference = this.getBaseTrackingReference(initialEventName, appLocation);
    return this.extendTrackingReference(trackingReference, requiredProps);
  }
}

// Export a singleton TrackingHelper
export const trackingHelper = new TrackingHelper();
